import { useAtom, useAtomValue } from 'jotai'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TabbedWidgetWrapper } from '../../rapid-ui'
import { useIsAiGeneratedImage } from '../ai-generated-label'
import { landingPageImageIdAtom, landingPageMetaForceAiGeneratedLabelAtom } from '../state'
import { AiGeneratedLabelToggle } from './ai-generated-label-toggle'
import { LandingPageImageWidget } from './landing-page-image-widget'
import { LandingPageLayerManager } from './landing-page-layer-manager'

export type LandingPageWidgetChildrenHelpers = {
	widget: ReactNode
}

export interface LandingPageWidgetProps {
	children: (helpers: LandingPageWidgetChildrenHelpers) => ReactNode
	globalTools: ReactNode
}

export function LandingPageWidget({ children, globalTools }: LandingPageWidgetProps) {
	const { t } = useTranslation('common')
	const [forceAiGeneratedLabel, setForceAiGeneratedLabel] = useAtom(landingPageMetaForceAiGeneratedLabelAtom)
	const imageId = useAtomValue(landingPageImageIdAtom)
	const aiImageryDetected = useIsAiGeneratedImage(imageId)

	const tabs = [
		{
			id: 'image',
			label: t('editor.widgets.image'),
			children: <LandingPageImageWidget />,
		},
		{
			id: 'layers',
			label: t('editor.widgets.layers'),
			children: <LandingPageLayerManager />,
		},
		{
			id: 'tools',
			label: t('editor.widgets.tools'),
			children: (
				<>
					{globalTools}
					<div className="flex flex-col divide-y divide-grey-100 mt-3 text-left">
						<AiGeneratedLabelToggle
							forced={forceAiGeneratedLabel ?? false}
							onForcedChange={setForceAiGeneratedLabel}
							detected={aiImageryDetected}
						/>
					</div>
				</>
			),
		},
	]
	const [currentTab, setCurrentTab] = useState<typeof tabs[number]['id']>('image')

	const widget = (
		<>
			<TabbedWidgetWrapper
				value={currentTab}
				onChange={(v) => setCurrentTab(v)}
				tabs={tabs}
				name={t('editor.widgets.landingPage')}
				animateHeight={false}
			/>
		</>
	)

	return (
		<>
			{children({ widget })}
		</>
	)
}
